import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdSearch,
  MdOutlineCloudDownload,
  MdClose,
  MdOutlineRemoveRedEye,
  MdCheck,
  MdDelete,
  MdBlock,
  MdArrowBack,
  MdOutlineThumbUp,
  MdOutlineReportProblem,
  MdChatBubbleOutline,
  MdOutlineModeComment,
  MdOutlineVisibilityOff,
  MdOutlineHourglassEmpty,
  MdDevices,
  MdPerson,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import { formatDate } from '../utils/format';

export default function Comments() {
  const location = useLocation();
  // View mode ('overview' | 'reported')
  const [viewMode, setViewMode] = useState('overview');
  const [selectedComment, setSelectedComment] = useState(null);

  // Summary stats state
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    reported: 0,
    hidden: 0,
    pending: 0,
  });

  // Main Comments table state (Direct Recent Comments)
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentSearch, setCommentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('Newest First');
  const [dateRange, setDateRange] = useState('All Time');
  const [commentPage, setCommentPage] = useState(1);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentPages, setCommentPages] = useState(1);

  // Multi-select for bulk actions
  const [selectedCommentIds, setSelectedCommentIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  // Reported Comments list state (for dedicated reported screen)
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportSearch, setReportSearch] = useState('');
  const [reportStatusFilter, setReportStatusFilter] = useState('pending');
  const [reportPage, setReportPage] = useState(1);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportPages, setReportPages] = useState(1);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);

  // Fetch summary statistics
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/comments/stats');
      if (res.data?.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch comment stats:', err);
    }
  }, []);

  // Fetch Direct Recent Comments (Newest First)
  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const params = {
        page: commentPage,
        limit: 15,
        search: commentSearch,
        status: statusFilter === 'All' ? undefined : statusFilter,
        sort: sortBy === 'Newest First' ? 'newest' : sortBy === 'Oldest First' ? 'oldest' : 'most_likes',
        date_range: dateRange === 'All Time' ? 'all' : dateRange === 'Today' ? 'today' : dateRange === 'Last 7 Days' ? 'week' : 'month',
      };

      const res = await api.get('/admin/comments', { params });
      if (res.data?.data) {
        setComments(res.data.data);
        setCommentTotal(res.data.pagination?.total || res.data.data.length);
        setCommentPages(res.data.pagination?.pages || 1);
      }
    } catch (err) {
      toast.error('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  }, [commentPage, commentSearch, statusFilter, sortBy, dateRange]);

  // Fetch Reported Comments
  const fetchReportedComments = useCallback(async () => {
    setReportsLoading(true);
    try {
      const res = await api.get('/admin/comments/reports', {
        params: {
          page: reportPage,
          limit: 10,
          status: reportStatusFilter,
          search: reportSearch,
        }
      });
      if (res.data?.data) {
        setReports(res.data.data);
        setReportTotal(res.data.pagination?.total || res.data.data.length);
        setReportPages(res.data.pagination?.pages || 1);
      }
    } catch (err) {
      toast.error('Failed to load reported comments');
    } finally {
      setReportsLoading(false);
    }
  }, [reportPage, reportStatusFilter, reportSearch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    if (searchFromUrl) {
      setCommentSearch(searchFromUrl);
      setCommentPage(1);
    }
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const idFromUrl = searchParams.get('id') || searchParams.get('comment_id');
    if (idFromUrl && comments.length > 0) {
      const match = comments.find(c => String(c.id) === String(idFromUrl));
      if (match) {
        setSelectedComment(match);
      }
    }
  }, [location.search, comments]);

  useEffect(() => {
    if (viewMode === 'overview') {
      fetchComments();
    } else if (viewMode === 'reported') {
      fetchReportedComments();
    }
  }, [viewMode, fetchComments, fetchReportedComments]);

  // Toggle single comment select
  const toggleSelectComment = (id) => {
    setSelectedCommentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Toggle all comments select
  const toggleSelectAllComments = () => {
    if (selectedCommentIds.length === comments.length) {
      setSelectedCommentIds([]);
    } else {
      setSelectedCommentIds(comments.map((c) => c.id));
    }
  };

  // Execute Bulk Action
  const handleBulkAction = async (actionType) => {
    if (selectedCommentIds.length === 0) {
      toast.error('Please select at least one comment');
      return;
    }

    try {
      await api.post('/admin/comments/bulk', {
        comment_ids: selectedCommentIds,
        action: actionType,
      });
      toast.success(`Selected comments processed successfully`);
      setSelectedCommentIds([]);
      setBulkAction('');
      fetchComments();
      fetchStats();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  // Moderate Single Comment
  const handleModerateComment = async (commentId, action) => {
    try {
      if (action === 'delete') {
        await api.patch(`/admin/comments/${commentId}/delete`);
      } else {
        await api.patch(`/admin/comments/${commentId}/status`, { action });
      }
      toast.success(`Comment ${action === 'approve' ? 'approved/unhidden' : action === 'hide' ? 'hidden' : 'deleted'}`);
      
      if (selectedComment && selectedComment.id === commentId) {
        setSelectedComment((prev) => ({
          ...prev,
          status: action === 'approve' ? 'Approved' : action === 'hide' ? 'Hidden' : 'Deleted',
          is_deleted: action !== 'approve',
        }));
      }
      fetchComments();
      fetchStats();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  // Actions for Reported Comments view
  const handleResolveReport = async (reportId) => {
    try {
      await api.patch(`/admin/comments/reports/${reportId}/resolve`);
      toast.success('Report marked as resolved');
      fetchReportedComments();
      fetchStats();
    } catch (err) {
      toast.error('Failed to resolve report');
    }
  };

  const handleHideReportedComment = async (commentId) => {
    try {
      await api.patch(`/admin/comments/${commentId}/status`, { action: 'hide' });
      toast.success('Comment hidden successfully');
      fetchReportedComments();
      fetchStats();
    } catch (err) {
      toast.error('Failed to hide comment');
    }
  };

  const handleDeleteReportedComment = async (commentId) => {
    try {
      await api.patch(`/admin/comments/${commentId}/delete`);
      toast.success('Comment deleted successfully');
      fetchReportedComments();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div className="page" style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700' }}>Comments Moderation</h2>
          <p style={{ margin: 0, color: '#64748B', fontSize: '14px' }}>Admin Panel / Comments Management</p>
        </div>
      </div>

      {/* ===== SECTION 1: DASHBOARD SUMMARY CARDS ===== */}
      <div className="comments-summary-grid">
        {/* Total Comments Card */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setViewMode('overview');
            setStatusFilter('All');
            setDateRange('All Time');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer' }}
          title="Click to show all comments"
        >
          <div className="summary-card-icon blue">
            <MdChatBubbleOutline />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block' }}>
              Total Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', lineHeight: '1.2' }}>
              {stats.total?.toLocaleString() || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#94A3B8', marginTop: '2px', display: 'block' }}>
              All time
            </span>
          </div>
        </div>

        {/* Today's Comments Card */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setViewMode('overview');
            setStatusFilter('All');
            setDateRange('Today');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer' }}
          title="Click to show today's comments"
        >
          <div className="summary-card-icon green">
            <MdOutlineModeComment />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block' }}>
              Today's Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', lineHeight: '1.2' }}>
              {stats.today || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#16A34A', fontWeight: '600', marginTop: '2px', display: 'block' }}>
              Filter by Today
            </span>
          </div>
        </div>

        {/* Reported Comments Card */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setViewMode('overview');
            setStatusFilter('Reported');
            setCommentPage(1);
          }}
          style={{
            cursor: 'pointer',
            border: statusFilter === 'Reported' ? '2px solid #F59E0B' : '1px solid #E2E8F0',
            transition: 'all 0.2s ease',
          }}
          title="Click to filter by reported comments"
        >
          <div className="summary-card-icon amber">
            <MdOutlineReportProblem />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block' }}>
              Reported Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', lineHeight: '1.2' }}>
              {stats.reported || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#D97706', fontWeight: '700', marginTop: '2px', display: 'block' }}>
              Needs review → Click to filter
            </span>
          </div>
        </div>

        {/* Hidden Comments Card */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setViewMode('overview');
            setStatusFilter('Hidden');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer' }}
          title="Click to show hidden comments"
        >
          <div className="summary-card-icon purple">
            <MdOutlineVisibilityOff />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block' }}>
              Hidden Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', lineHeight: '1.2' }}>
              {stats.hidden || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#9333EA', marginTop: '2px', display: 'block' }}>
              By admin moderation
            </span>
          </div>
        </div>

        {/* Pending Moderation Card */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setViewMode('overview');
            setStatusFilter('Pending');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer' }}
          title="Click to show pending comments"
        >
          <div className="summary-card-icon red">
            <MdOutlineHourglassEmpty />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block' }}>
              Pending Moderation
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', lineHeight: '1.2' }}>
              {stats.pending || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#EF233C', fontWeight: '600', marginTop: '2px', display: 'block' }}>
              Requires action
            </span>
          </div>
        </div>
      </div>

      {/* ===== SECTION 2: REPORTED COMMENTS DEDICATED VIEW OR RECENT COMMENTS TABLE ===== */}
      {viewMode === 'reported' ? (
        <div className="comments-table-wrapper" style={{ marginTop: 24 }}>
          {/* Top Bar with Back Button */}
          <div className="comments-filter-bar" style={{ flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setViewMode('overview')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}
              >
                <MdArrowBack size={18} /> Back to Recent Comments
              </button>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdOutlineReportProblem color="#D97706" size={22} /> Reported Comments Flags
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748B' }}>
                  Review user flags, investigate report reasons, and take moderation action.
                </p>
              </div>
            </div>

            {/* Search Input */}
            <div className="comments-search-input" style={{ width: 280 }}>
              <MdSearch className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search article, comment, reporter..."
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Status Tabs */}
          <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderBottom: '1px solid #E2E8F0', background: '#FAFAFC' }}>
            {[
              { id: 'pending', label: 'Pending Review', color: '#D97706', bg: '#FEF3C7' },
              { id: 'resolved', label: 'Resolved / Dismissed', color: '#16A34A', bg: '#DCFCE7' },
              { id: 'hidden', label: 'Hidden / Deleted', color: '#DC2626', bg: '#FEE2E2' },
              { id: 'all', label: 'All Reports', color: '#475569', bg: '#F1F5F9' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setReportStatusFilter(tab.id); setReportPage(1); }}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: reportStatusFilter === tab.id ? `2px solid ${tab.color}` : '1px solid #CBD5E1',
                  background: reportStatusFilter === tab.id ? tab.bg : '#FFFFFF',
                  color: reportStatusFilter === tab.id ? tab.color : '#64748B',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Reported Comments Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#FAFAFC', borderBottom: '1px solid #E9EDF5', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '700' }}>
                  <th style={{ padding: '12px 16px', width: '40px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Article</th>
                  <th style={{ padding: '12px 16px' }}>Comment & Author</th>
                  <th style={{ padding: '12px 16px' }}>Reporter & Reason</th>
                  <th style={{ padding: '12px 16px' }}>Report Date</th>
                  <th style={{ padding: '12px 16px' }}>Reports Count</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportsLoading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '36px', color: '#64748B' }}>
                      Loading reported comments...
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '36px', color: '#64748B' }}>
                      No reported comments found for status: <strong>{reportStatusFilter}</strong>.
                    </td>
                  </tr>
                ) : (
                  reports.map((rep, idx) => (
                    <tr key={rep.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                        {(reportPage - 1) * 10 + idx + 1}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 260 }}>
                          {rep.article_thumbnail && (
                            <img
                              src={rep.article_thumbnail}
                              alt=""
                              style={{ width: 44, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #E2E8F0', flexShrink: 0 }}
                            />
                          )}
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={rep.article_title}>
                            {rep.article_title || 'Untitled Article'}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', maxWidth: 280 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B', marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          "{rep.comment_body}"
                        </div>
                        <div style={{ fontSize: 11.5, color: '#64748B', fontWeight: 600 }}>
                          By: <span style={{ color: '#0F172A' }}>{rep.comment_author}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#DC2626', marginBottom: 2 }}>
                          🚩 {rep.reason}
                        </div>
                        <div style={{ fontSize: 11.5, color: '#64748B' }}>
                          Reported by: <strong>{rep.reporter_name}</strong>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatDate(rep.created_at)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 12, background: '#FEF3C7', color: '#B45309', fontSize: 12, fontWeight: 800 }}>
                          🚩 {rep.reports_count}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {rep.is_comment_deleted ? (
                          <span style={{ padding: '4px 10px', borderRadius: 12, background: '#FEE2E2', color: '#DC2626', fontSize: 11.5, fontWeight: 700 }}>Hidden / Deleted</span>
                        ) : rep.is_resolved ? (
                          <span style={{ padding: '4px 10px', borderRadius: 12, background: '#DCFCE7', color: '#15803D', fontSize: 11.5, fontWeight: 700 }}>Resolved</span>
                        ) : (
                          <span style={{ padding: '4px 10px', borderRadius: 12, background: '#FEF3C7', color: '#D97706', fontSize: 11.5, fontWeight: 700 }}>Pending Review</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => setSelectedReportDetail(rep)}
                            title="View Details"
                          >
                            <MdOutlineRemoveRedEye size={16} />
                          </button>

                          {!rep.is_resolved && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleResolveReport(rep.id)}
                              title="Dismiss / Resolve Report"
                              style={{ background: '#22C55E', color: '#FFF', border: 'none', padding: '4px 8px', borderRadius: 6 }}
                            >
                              <MdCheck size={16} />
                            </button>
                          )}

                          {!rep.is_comment_deleted && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleHideReportedComment(rep.comment_id)}
                              title="Hide Comment"
                              style={{ background: '#F59E0B', color: '#FFF', border: 'none', padding: '4px 8px', borderRadius: 6 }}
                            >
                              <MdOutlineVisibilityOff size={16} />
                            </button>
                          )}

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteReportedComment(rep.comment_id)}
                            title="Delete Comment"
                            style={{ background: '#EF4444', color: '#FFF', border: 'none', padding: '4px 8px', borderRadius: 6 }}
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination for Reported list */}
          {reportPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: 13, color: '#64748B' }}>
                Page {reportPage} of {reportPages} ({reportTotal} total reports)
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={reportPage <= 1}
                  onClick={() => setReportPage(p => Math.max(p - 1, 1))}
                >
                  <MdChevronLeft size={18} />
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={reportPage >= reportPages}
                  onClick={() => setReportPage(p => Math.min(p + 1, reportPages))}
                >
                  <MdChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ===== DIRECT RECENT COMMENTS TABLE (REDESIGNED LAYOUT) ===== */
        <div className="comments-table-wrapper">
          {/* Header & Controls */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #E9EDF5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#FAFAFC',
              flexWrap: 'wrap',
              gap: 12
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0F172A' }}>
                Recent Comments
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748B' }}>
                Direct live comment feed sorted newest first for immediate moderation
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => {
                  setViewMode('reported');
                  setReportStatusFilter('pending');
                  setReportPage(1);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#D97706',
                  border: '1px solid #FCD34D',
                  borderRadius: '8px',
                  background: '#FEF3C7',
                  cursor: 'pointer',
                }}
              >
                <MdOutlineReportProblem size={16} /> View Reported Flags ({stats.reported || 0})
              </button>
            </div>
          </div>

          {/* Filters & Search Bar */}
          <div className="comments-filter-bar" style={{ flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', flex: 1, minWidth: 280 }}>
              <div className="comments-search-input" style={{ width: '100%', maxWidth: 320 }}>
                <MdSearch className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search comment, article, user name..."
                  value={commentSearch}
                  onChange={(e) => {
                    setCommentSearch(e.target.value);
                    setCommentPage(1);
                  }}
                />
              </div>

              <select
                className="comments-select-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCommentPage(1);
                }}
              >
                <option value="All">Status: All</option>
                <option value="Approved">Approved</option>
                <option value="Reported">Reported</option>
                <option value="Hidden">Hidden</option>
                <option value="Pending">Pending</option>
              </select>

              <select
                className="comments-select-filter"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCommentPage(1);
                }}
              >
                <option value="Newest First">Sort: Newest First</option>
                <option value="Oldest First">Sort: Oldest First</option>
                <option value="Most Likes">Sort: Most Likes</option>
              </select>

              <select
                className="comments-select-filter"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setCommentPage(1);
                }}
              >
                <option value="All Time">Date: All Time</option>
                <option value="Today">Today</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {selectedCommentIds.length > 0 && (
                <select
                  className="comments-select-filter"
                  style={{ background: '#EF233C', color: '#ffffff', fontWeight: '600', border: 'none' }}
                  value={bulkAction}
                  onChange={(e) => {
                    setBulkAction(e.target.value);
                    if (e.target.value) handleBulkAction(e.target.value);
                  }}
                >
                  <option value="">Bulk Actions ({selectedCommentIds.length})</option>
                  <option value="approve">Approve Selected</option>
                  <option value="hide">Hide Selected</option>
                  <option value="delete">Delete Selected</option>
                </select>
              )}
            </div>
          </div>

          {/* Comments Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#FAFAFC', borderBottom: '1px solid #E9EDF5', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '700' }}>
                  <th style={{ padding: '12px 16px', width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={comments.length > 0 && selectedCommentIds.length === comments.length}
                      onChange={toggleSelectAllComments}
                    />
                  </th>
                  <th style={{ padding: '12px 16px', minWidth: '220px' }}>Comment</th>
                  <th style={{ padding: '12px 16px', minWidth: '180px' }}>Article</th>
                  <th style={{ padding: '12px 16px', minWidth: '150px' }}>Commented By</th>
                  <th style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>Posted On</th>
                  <th style={{ padding: '12px 16px', width: '70px' }}>Likes</th>
                  <th style={{ padding: '12px 16px', width: '70px' }}>Reports</th>
                  <th style={{ padding: '12px 16px', width: '100px' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', minWidth: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commentsLoading ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '36px', color: '#64748B' }}>
                      Loading comments...
                    </td>
                  </tr>
                ) : comments.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '36px', color: '#64748B' }}>
                      No comments found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  comments.map((c) => (
                    <tr
                      key={c.id}
                      style={{
                        borderBottom: '1px solid #F1F5F9',
                        background: selectedComment?.id === c.id ? '#FEF2F2' : 'transparent',
                        transition: 'background 0.15s ease',
                      }}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedCommentIds.includes(c.id)}
                          onChange={() => toggleSelectComment(c.id)}
                        />
                      </td>

                      {/* 1. Comment Text */}
                      <td style={{ padding: '14px 16px', maxWidth: '280px' }}>
                        <div
                          style={{
                            fontSize: '13.5px',
                            color: '#0F172A',
                            fontWeight: '500',
                            lineHeight: '1.4',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                          title={c.body}
                        >
                          {c.body}
                        </div>
                        {c.is_reply && (
                          <span style={{ fontSize: '10.5px', background: '#F1F5F9', color: '#475569', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>
                            Reply
                          </span>
                        )}
                      </td>

                      {/* 2. Article Title */}
                      <td style={{ padding: '14px 16px', maxWidth: '220px' }}>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#2563EB',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                          title={c.article_title || 'Untitled Article'}
                        >
                          {c.article_title || 'Untitled Article'}
                        </div>
                      </td>

                      {/* 3. Commented By */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              background: c.is_guest ? '#F1F5F9' : '#EFF6FF',
                              color: c.is_guest ? '#64748B' : '#2563EB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              flexShrink: 0,
                            }}
                          >
                            {c.is_guest ? <MdDevices /> : <MdPerson />}
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A', whiteSpace: 'nowrap' }}>
                              {c.author}
                            </div>
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: '700',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                background: c.is_guest ? '#F8FAFC' : '#DCFCE7',
                                color: c.is_guest ? '#64748B' : '#15803D',
                                border: c.is_guest ? '1px solid #E2E8F0' : '1px solid #86EFAC',
                                display: 'inline-block',
                                marginTop: '1px'
                              }}
                            >
                              {c.is_guest ? 'Guest' : 'Registered'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* 4. Posted On */}
                      <td style={{ padding: '14px 16px', fontSize: '12.5px', color: '#64748B', whiteSpace: 'nowrap', fontWeight: '500' }}>
                        {formatDate(c.created_at)}
                      </td>

                      {/* 5. Likes */}
                      <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#16A34A' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {c.like_count || 0} <MdOutlineThumbUp size={14} />
                        </span>
                      </td>

                      {/* 6. Reports */}
                      <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: c.reports_count > 0 ? '#EF233C' : '#64748B' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {c.reports_count || 0} {c.reports_count > 0 && <MdOutlineReportProblem size={14} />}
                        </span>
                      </td>

                      {/* 7. Status */}
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11.5px',
                            fontWeight: '700',
                            background: c.is_deleted ? '#FEE2E2' : c.reports_count > 0 ? '#FEF3C7' : '#DCFCE7',
                            color: c.is_deleted ? '#DC2626' : c.reports_count > 0 ? '#D97706' : '#15803D',
                            display: 'inline-block'
                          }}
                        >
                          {c.status || (c.is_deleted ? 'Hidden' : 'Approved')}
                        </span>
                      </td>

                      {/* 8. Actions */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                          {/* View Button */}
                          <button
                            title="View Comment Details"
                            onClick={() => setSelectedComment(c)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #CBD5E1',
                              background: '#ffffff',
                              color: '#334155',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <MdOutlineRemoveRedEye size={14} /> View
                          </button>

                          {/* Hide / Unhide Button */}
                          <button
                            title={c.is_deleted ? 'Unhide Comment' : 'Hide Comment'}
                            onClick={() => handleModerateComment(c.id, c.is_deleted ? 'approve' : 'hide')}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: c.is_deleted ? '1px solid #10B981' : '1px solid #F59E0B',
                              background: c.is_deleted ? '#ECFDF5' : '#FFFBEB',
                              color: c.is_deleted ? '#059669' : '#D97706',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {c.is_deleted ? (
                              <><MdCheck size={14} /> Unhide</>
                            ) : (
                              <><MdOutlineVisibilityOff size={14} /> Hide</>
                            )}
                          </button>

                          {/* Delete Button */}
                          <button
                            title="Delete Comment"
                            onClick={() => handleModerateComment(c.id, 'delete')}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #FCA5A5',
                              background: '#FEF2F2',
                              color: '#EF233C',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <MdDelete size={14} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Comments Pagination */}
          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid #E9EDF5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '13px',
              color: '#64748B',
            }}
          >
            <span>
              Showing 1 to {comments.length} of {commentTotal} comments
            </span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button
                disabled={commentPage === 1}
                onClick={() => setCommentPage((p) => Math.max(1, p - 1))}
                style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', background: '#fff', cursor: commentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <MdChevronLeft size={18} />
              </button>
              <span style={{ fontWeight: '600', color: '#0F172A', padding: '0 8px' }}>
                Page {commentPage} of {commentPages}
              </span>
              <button
                disabled={commentPage === commentPages || commentPages === 0}
                onClick={() => setCommentPage((p) => Math.min(commentPages, p + 1))}
                style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', background: '#fff', cursor: commentPage === commentPages || commentPages === 0 ? 'not-allowed' : 'pointer' }}
              >
                <MdChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SECTION 3: COMMENT DETAILS DRAWER / MODAL ===== */}
      {selectedComment && (
        <>
          <div
            className="comment-sidebar-backdrop"
            onClick={() => setSelectedComment(null)}
          />
          <div className="comment-detail-drawer">
            <div className="comment-drawer-header">
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0F172A' }}>
                Comment Details
              </h3>
              <button
                onClick={() => setSelectedComment(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748B' }}
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="comment-drawer-body">
              {/* User Metadata */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '10px',
                    background: '#EFF6FF',
                    color: '#2563EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                  }}
                >
                  {selectedComment.is_guest ? <MdDevices /> : <MdPerson />}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                    {selectedComment.author}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>
                    {selectedComment.ip_address}
                  </div>
                </div>
              </div>

              {/* Article Context */}
              <div style={{ marginBottom: '16px', padding: '10px 12px', background: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#1D4ED8', textTransform: 'uppercase', display: 'block' }}>
                  Article
                </span>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1E40AF', marginTop: '2px' }}>
                  {selectedComment.article_title || 'Untitled Article'}
                </div>
              </div>

              {/* Comment Content */}
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '6px' }}>
                  Comment Text
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#0F172A',
                    lineHeight: '1.5',
                    background: '#FAFAFC',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #E9EDF5',
                  }}
                >
                  {selectedComment.body}
                </p>
              </div>

              {/* Metadata Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Likes</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {selectedComment.like_count || 0} <MdOutlineThumbUp size={14} />
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Reports</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: selectedComment.reports_count > 0 ? '#EF233C' : '#0F172A' }}>
                    {selectedComment.reports_count || 0}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Status</span>
                  <span className={`comment-status-badge ${selectedComment.status.toLowerCase()}`} style={{ marginTop: '2px' }}>
                    {selectedComment.status}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Posted On</span>
                  <span style={{ fontSize: '12.5px', fontWeight: '600', color: '#0F172A' }}>
                    {formatDate(selectedComment.created_at)}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Device</span>
                  <span style={{ fontSize: '13px', color: '#334155' }}>{selectedComment.device || 'Android'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Browser</span>
                  <span style={{ fontSize: '13px', color: '#334155' }}>{selectedComment.browser || 'Chrome 126'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>Country</span>
                  <span style={{ fontSize: '13px', color: '#334155' }}>{selectedComment.country || 'India'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: '#64748B', display: 'block' }}>User Type</span>
                  <span style={{ fontSize: '13px', color: '#334155' }}>{selectedComment.user_type || 'Guest'}</span>
                </div>
              </div>
            </div>

            {/* Moderation Actions Footer */}
            <div className="comment-drawer-footer">
              <button
                onClick={() => handleModerateComment(selectedComment.id, 'approve')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Approve
              </button>
              <button
                onClick={() => handleModerateComment(selectedComment.id, selectedComment.is_deleted ? 'approve' : 'hide')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#F59E0B',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {selectedComment.is_deleted ? 'Unhide' : 'Hide'}
              </button>
              <button
                onClick={() => handleModerateComment(selectedComment.id, 'delete')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#EF233C',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== REPORTED COMMENT DETAIL MODAL ===== */}
      {selectedReportDetail && (
        <div className="modal-overlay" onClick={() => setSelectedReportDetail(null)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()} style={{ padding: 24, borderRadius: 16, background: '#FFF', maxWidth: 540, width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E2E8F0', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdOutlineReportProblem color="#D97706" size={22} /> Reported Comment Details
              </h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setSelectedReportDetail(null)} style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                <MdClose size={20} />
              </button>
            </div>

            {/* Article Box */}
            <div style={{ padding: 12, borderRadius: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
              {selectedReportDetail.article_thumbnail && (
                <img src={selectedReportDetail.article_thumbnail} alt="" style={{ width: 50, height: 40, objectFit: 'cover', borderRadius: 6 }} />
              )}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Article</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{selectedReportDetail.article_title}</div>
              </div>
            </div>

            {/* Comment Box */}
            <div style={{ padding: 14, borderRadius: 10, background: '#FFF', border: '1px solid #E2E8F0', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>Comment Content</div>
              <p style={{ fontSize: 14, color: '#1E293B', fontWeight: 500, margin: 0, lineHeight: 1.5 }}>
                "{selectedReportDetail.comment_body}"
              </p>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 8, fontWeight: 600 }}>
                By: <strong style={{ color: '#0F172A' }}>{selectedReportDetail.comment_author}</strong> ({selectedReportDetail.comment_author_phone})
              </div>
            </div>

            {/* Report Reason & Details Box */}
            <div style={{ padding: 14, borderRadius: 10, background: '#FEF3C7', border: '1px solid #FCD34D', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#B45309', marginBottom: 4 }}>
                🚩 Report Reason: {selectedReportDetail.reason}
              </div>
              <div style={{ fontSize: 12, color: '#92400E' }}>
                Reported by: <strong>{selectedReportDetail.reporter_name}</strong> ({selectedReportDetail.reporter_phone})
              </div>
              <div style={{ fontSize: 11.5, color: '#B45309', marginTop: 4 }}>
                Date: {formatDate(selectedReportDetail.created_at)} • Total Reports: {selectedReportDetail.reports_count}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedReportDetail(null)}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FFF', cursor: 'pointer' }}
              >
                Close
              </button>
              {!selectedReportDetail.is_resolved && (
                <button
                  className="btn btn-success"
                  onClick={() => { handleResolveReport(selectedReportDetail.id); setSelectedReportDetail(null); }}
                  style={{ background: '#22C55E', color: '#FFF', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  Dismiss Report
                </button>
              )}
              {!selectedReportDetail.is_comment_deleted && (
                <button
                  className="btn btn-warning"
                  onClick={() => { handleHideReportedComment(selectedReportDetail.comment_id); setSelectedReportDetail(null); }}
                  style={{ background: '#F59E0B', color: '#FFF', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  Hide Comment
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
